import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { RotateCw } from 'lucide-react'
import { rpc } from '@/api/rpc'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { formatWhen } from '@/lib/format'

type Channel = { pending: number; failed: number; last_success: string | null }
type Health = {
  dropbox: Channel
  push: Channel
  email: Channel
  recent_failures: {
    kind: string
    id: string
    what: string | null
    last_error: string | null
    attempts: number
    at: string
  }[]
}

export const Route = createFileRoute('/_app/admin/sync')({ component: Sync })

/** Dropbox, push and email job health with retry. */
function Sync() {
  const queryClient = useQueryClient()
  const { data, error } = useQuery({
    queryKey: ['sync-health'],
    queryFn: () => rpc<Health>('sync_health'),
    refetchInterval: 30_000,
  })
  const retry = useMutation({
    mutationFn: (j: { kind: string; id: string }) =>
      rpc('admin_retry_job', {
        kind: j.kind === 'dropbox' ? 'dropbox' : 'notification',
        job_id: j.id,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sync-health'] }),
  })
  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl uppercase">Sync health</h1>
      {error && <p className="font-semibold text-danger">{error.message}</p>}
      <div className="grid gap-4 md:grid-cols-3">
        {data &&
          (['dropbox', 'push', 'email'] as const).map((k) => (
            <Card key={k} className={data[k].failed ? 'border-2 border-danger' : undefined}>
              <CardTitle>{k === 'dropbox' ? 'Dropbox' : k === 'push' ? 'Push' : 'Email'}</CardTitle>
              <p className="mt-2">
                {data[k].pending} pending - {data[k].failed} failed
              </p>
              <p className="text-sm text-fg-muted">
                Last success: {data[k].last_success ? formatWhen(data[k].last_success) : 'never'}
              </p>
            </Card>
          ))}
      </div>
      <Card className="flex flex-col gap-3">
        <CardTitle>Recent failures</CardTitle>
        {data?.recent_failures.length === 0 && <p className="text-fg-muted">None.</p>}
        {data?.recent_failures.map((f) => (
          <div key={f.id} className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <div className="flex-1">
              <p className="font-semibold">
                {f.kind}: {f.what}
              </p>
              <p className="text-sm text-fg-muted">
                {f.last_error} - {f.attempts} attempts - {formatWhen(f.at)}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => retry.mutate(f)}>
              <RotateCw aria-hidden /> Retry
            </Button>
          </div>
        ))}
      </Card>
    </div>
  )
}
